if (!customElements.get("product-info")) {
  class ProductInfo extends HTMLElement {
    abortController = undefined;
    requestSequence = 0;
    onVariantChangeUnsubscriber = undefined;
    pendingRequestUrl = null;
    preProcessHtmlCallbacks = [];
    postProcessHtmlCallbacks = [];

    constructor() {
      super();

      this.onWindowLoad = this.onWindowLoad.bind(this);
    }

    get productId() {
      return this.getAttribute('data-product-id');
    }

    get sectionId() {
      return this.dataset.originalSection || this.dataset.sectionId;
    }

    get disableSelectedVariantDefault() {
      return this.dataset.disableSelectedVariantDefault === "true" || false;
    }

    get enableVariantGroupImages() {
      return this.dataset.enableVariantGroupImages === "true" || false;
    }

    get enableHistoryState() {
      return this.dataset.enableHistoryState === "true" || false;
    }

    get viewMode() {
      return this.dataset.viewMode || 'main-product'
    }

    get pickupAvailability() {
      return this.querySelector(`pickup-availability`);
    }

    get productForm() {
      return this.querySelector('product-form'); // fallback: product-form.m-product-form--main
    }

    get productMedia() {
      return this.querySelector(`[id^="MediaGallery-${this.dataset.sectionId}"]`);
    }

    get variantPicker() {
      return this.querySelector('variant-picker');
    }

    get quantityInput() {
      return this.querySelector(`m-quantity-input input`);
    }
    get quantityInputStickyAtc() {
      return this.querySelector(`.m-sticky-addtocart m-quantity-input input`);
    }

    get stickyAtc() {
      return this.querySelector('sticky-atc');
    }

    connectedCallback() {
      if (this.viewMode === "main-product") {
        window.addEventListener("load", this.onWindowLoad);
      }

      this.init();
    }

    disconnectedCallback() {
      window.removeEventListener("load", this.onWindowLoad);
      this.requestSequence += 1;
      this.abortController?.abort();
      this.onVariantChangeUnsubscriber();
      this.cartUpdateUnsubscriber?.();
    }

    init() {
      this.currentVariant = this.getSelectedVariant(this);
      this.quantityInputs = [];

      if (this.quantityInput) {
        this.quantityInputs.push(this.quantityInput);
      }
      if (this.quantityInputStickyAtc) {
        this.quantityInputs.push(this.quantityInputStickyAtc);
      }

      if (this.disableSelectedVariantDefault) {
        this.handleDisableSelectedVariantDefault();
      }

      this.onVariantChangeUnsubscriber = MinimogEvents.subscribe(
        MinimogTheme.pubSubEvents.optionValueSelectionChange,
        this.handleOptionValueChange.bind(this)
      );

      this.showFeaturedMedia = this.dataset.showFeaturedMedia === "true" || false;

      this.initialMedias = this.productMedia
        ? this.productMedia.querySelectorAll(
            '.m-product-media--item:not(.swiper-slide-duplicate)'
          )
        : null;
      this.initialThumbs = this.productMedia
        ? this.productMedia.querySelectorAll(
            ".m-product-media--slider__thumbnails [data-media-type]:not(.swiper-slide-duplicate)"
          )
        : null;
      this.enablePackageMediaFilter =
        this.productMedia?.dataset.enablePackageMediaFilter === "true";
      this.enableInitialVariantFilter =
        this.productMedia?.dataset.enableInitialVariantFilter === "true";

      if (this.enableVariantGroupImages) {
        this.variantGroupImagesData = this.getVariantGroupImagesData();
        this.showFeaturedMedia = false;
      };

      this.initQuantityHandlers();

      if (
        this.currentVariant &&
        (!this.showFeaturedMedia ||
          this.enableVariantGroupImages ||
          this.enablePackageMediaFilter ||
          this.enableInitialVariantFilter)
      ) {
        this.updateMedia(this.currentVariant);
      } else {
        this.productMedia?.revealMedia?.();
      }
    }

    getVariantGroupImagesData() {
      const data = this.querySelector('#variantGroup[type="application/json"]');
      if (!data) return {};

      try {
        return JSON.parse(data.textContent) || {};
      } catch (error) {
        console.warn("[product-info] Unable to parse variant media mapping.", error);
        return {};
      }
    }

    handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
      if (!this.contains(event.target)) return;

      const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
      const shouldSwapProduct = this.dataset.url !== productUrl;
      // this.pendingRequestUrl = productUrl;
      const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

      this.renderProductInfo({
        requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
        targetId: target.id,
        callback: shouldSwapProduct
          ? this.handleSwapProduct(productUrl, shouldFetchFullPage, this.viewMode)
          : this.handleUpdateProductInfo(productUrl, this.viewMode),
      });
    }

    renderProductInfo({ requestUrl, targetId, callback }) {
      this.abortController?.abort();
      const requestSequence = ++this.requestSequence;
      this.abortController = new AbortController();

      fetch(requestUrl, { signal: this.abortController.signal })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`Product info request failed: ${response.status}`);
          }

          return response.text();
        })
        .then((responseText) => {
          if (requestSequence !== this.requestSequence) return false;

          this.pendingRequestUrl = null;
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          callback(html);
          return true;
        })
        .then((shouldRestoreFocus) => {
          if (!shouldRestoreFocus || requestSequence !== this.requestSequence) return;

          // set focus to last clicked option value
          document.querySelector(`#${targetId}`)?.focus();
        })
        .catch((error) => {
          if (error.name === 'AbortError') {
            console.log('Fetch aborted by user');
          } else {
            console.error(error);
          }
        });
    }

    buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
      const params = [];

      !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

      if (optionValues.length) {
        params.push(`option_values=${optionValues.join(',')}`);
      }

      return `${url}?${params.join('&')}`;
    }

    handleUpdateProductInfo(productUrl, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const variant = this.getSelectedVariant(html);

        this.pickupAvailability?.update(variant);
        this.updateOptionValues(html);
        this.updateURL(productUrl, variant?.id);
        this.updateVariantInputs(variant?.id);

        if (!variant) {
          this.setUnavailable();
          return;
        }

        this.updateMedia(variant);

        const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
          const source = html.getElementById(`${id}-${this.sectionId}`);
          const destination = this.querySelector(`#${id}-${this.dataset.sectionId}`);
          if (source && destination) {
            destination.innerHTML = source.innerHTML;
            destination.classList.toggle('m:hidden', shouldHide(source));
          }
        };

        updateSourceFromDestination('Price');
        updateSourceFromDestination("Inventory");
        updateSourceFromDestination("Sku");
        updateSourceFromDestination("Availability");
        updateSourceFromDestination("Volume");
        updateSourceFromDestination('PricePerItem');

        this.updateQuantityRules(this.sectionId, this.productId, html);
        this.querySelector(`#QuantityRules-${this.dataset.section}`)?.classList.remove('m:hidden');
        this.querySelector(`#VolumeNote-${this.dataset.section}`)?.classList.remove('m:hidden');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.MinimogStrings.soldOut
        );

        this.currentVariant = variant;

        MinimogEvents.emit(MinimogTheme.pubSubEvents.variantChange, {
          data: { variant, sectionId: this.sectionId, html },
        });

        document.dispatchEvent(
          new CustomEvent('variant:changed', {
            detail: {
              variant: this.currentVariant,
            },
          })
        );
      };
    }

    handleSwapProduct(productUrl, updateFullPage, viewMode) {
      return (html) => {
        const quickView = html.querySelector('#MainProduct-quick-view__content');
        if (quickView && viewMode === 'quick-view') {
          html = new DOMParser().parseFromString(quickView.innerHTML, "text/html");
        }

        const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
        const productInfo = html.querySelector(selector);
        const variant = this.getSelectedVariant(productInfo);
        productInfo.dataset.disableSelectedVariantDefault = false;

        this.updateURL(productUrl, variant?.id);

        if (updateFullPage) {
          document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;
          HTMLUpdateUtility.viewTransition(
            document.querySelector('main'),
            html.querySelector('main'),
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        } else {
          HTMLUpdateUtility.viewTransition(
            this,
            productInfo,
            this.preProcessHtmlCallbacks,
            this.postProcessHtmlCallbacks
          );
        }

        this.currentVariant = variant;
      };
    }

    getSelectedVariant(productInfoNode) {
      const selectedVariant = productInfoNode.querySelector('variant-picker [data-selected-variant]')?.innerHTML;
      return !!selectedVariant ? JSON.parse(selectedVariant) : null;
    }

    updateURL(url, variantId) {
      if (this.dataset.updateUrl === 'false') return;
      window.history.replaceState({}, '', `${url}${this.enableHistoryState && variantId ? `?variant=${variantId}` : ''}`);
    }

    updateVariantInputs(variantId) {
      document
        .querySelectorAll(`#product-form-${this.dataset.sectionId}, #product-form-installment-${this.dataset.sectionId}`)
        .forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
    }

    updateOptionValues(html) {
      const variantPicker = html.querySelector('variant-picker');
      if (variantPicker) {
        HTMLUpdateUtility.viewTransition(this.variantPicker, variantPicker, this.preProcessHtmlCallbacks);
      }
    }

    setUnavailable() {
      const productForm = document.getElementById(
        `product-form-${this.sectionId}`
      );
      const addButton = productForm.querySelector('[name="add"]');

      if (addButton) {
        this.toggleAddButton(true, window.MinimogStrings.unavailable);
      }

      const selectors = [
        "Price",
        "Inventory",
        "Sku",
        "Availability"
      ]
        .map((id) => `#${id}-${this.sectionId}`)
        .join(", ");
      document
        .querySelectorAll(selectors)
        .forEach((selector) => selector.classList.add('m:hidden'));
    }

    toggleAddButton(disable = true, text, modifyClass = true) {
      const productForm = document.getElementById(`product-form-${this.dataset.sectionId}`);
      if (!productForm) return;
      const addButton = productForm.querySelector('[name="add"]');
      const addButtonText = productForm.querySelector('[name="add"] > span.m-add-to-cart--text');
      if (!addButton) return;

      if (disable) {
        addButton.setAttribute('disabled', 'disabled');
        if (text) addButtonText.textContent = text;
      } else {
        addButton.removeAttribute('disabled');
        addButtonText.innerHTML = window.MinimogStrings.addToCart;
      }

      if (!modifyClass) return;
    }

    updateMedia(variant) {
      if (!this.productMedia) return;
      if (
        this.enableVariantGroupImages ||
        this.enablePackageMediaFilter ||
        this.enableInitialVariantFilter
      ) {
        this.updatePackageMedia(variant);
      } else {
        const setActiveMedia = () => {
          if (typeof this.productMedia.setActiveMedia === 'function') {
            this.productMedia.init();
            this.productMedia.setActiveMedia(variant);
            return true; // Indicate success
          }
          return false; // Indicate failure
        };

        if (!setActiveMedia()) {
          this.timer = setInterval(() => {
            if (setActiveMedia()) {
              clearInterval(this.timer);
            }
          }, 100);
        }
      }
    }

    updatePackageMedia(variant) {
      if (!variant || !this.productMedia || !this.initialMedias) return;

      if (typeof window.pauseAllMedia === "function") {
        window.pauseAllMedia(this.productMedia);
      }
      if (typeof this.productMedia.prepareForFilteredMedia === "function") {
        this.productMedia.prepareForFilteredMedia();
      } else {
        this.productMedia.usesFilteredMedia = true;
      }
      const visibleMediaIds = this.resolveFilteredMediaIds(variant);
      const activeMediaId =
        variant.featured_media?.id || visibleMediaIds[0] || null;

      this.renderFilteredMedia(this.initialMedias, visibleMediaIds, false);
      this.renderFilteredMedia(this.initialThumbs, visibleMediaIds, true);
      this.productMedia.refreshFilteredMedia(activeMediaId);
    }

    handleDisableSelectedVariantDefault() {
      let urlParams = new URLSearchParams(window.location.search);
      if (urlParams.has("variant")) return;

      this.pickerFields = this.querySelectorAll("[data-picker-field]");

      this.pickerFields && this.pickerFields.forEach((field) => {
        let pickerType = field.dataset.pickerField;
        switch (pickerType) {
          case "select":
            let selectBox = field.querySelector("select");
            let option = document.createElement("option");
            option.text = this.dataset.variantOptionNoneText;
            option.setAttribute("disabled", "");
            option.setAttribute("selected", "");

            selectBox.add(option, 0);
            break;
          default:
            let checkedInputs = field.querySelectorAll("input:checked");
            checkedInputs &&
              checkedInputs.forEach(function (input) {
                input.removeAttribute("checked");
              });
            break;
        }

        field.dataset.selectedValue = "";
        if (field.querySelector(".option-label--selected")) field.querySelector(".option-label--selected").textContent = "";
      });

      this.setUnavailable();
      this.currentVariant = null;
    }

    normalizeMediaGroup(value) {
      return String(value || "")
        .trim()
        .toLowerCase()
        .normalize("NFKD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/×/g, "x")
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/-x-/g, "x")
        .replace(/^-|-$/g, "");
    }

    resolveFilteredMediaIds(variant) {
      const medias = Array.from(this.initialMedias || []);
      const allIds = medias.map((media) => String(media.dataset.mediaId));
      const sharedIds = medias
        .filter((media) => media.dataset.mediaGroup === "shared")
        .map((media) => String(media.dataset.mediaId));
      const mediaById = new Map(
        medias.map((media) => [String(media.dataset.mediaId), media])
      );

      const mappedVariant = this.variantGroupImagesData?.mapping?.find(
        (item) => Number(item.id) === Number(variant.id)
      );
      const mappedIds = Array.isArray(mappedVariant?.media)
        ? mappedVariant.media
            .map(String)
            .filter((mediaId) => mediaById.has(mediaId))
        : [];

      let packageIds = mappedIds;
      let formatIds = [];
      if (this.enablePackageMediaFilter) {
        const formatOptionIndex = Number(
          this.productMedia.dataset.formatOptionIndex || 0
        );
        const formatValue =
          variant.options?.[formatOptionIndex] || variant.option1 || "";
        const formatGroup = this.normalizeMediaGroup(formatValue);
        formatIds = medias
          .filter((media) => media.dataset.mediaFormat === formatGroup)
          .map((media) => String(media.dataset.mediaId));
      }

      if (packageIds.length === 0 && this.enablePackageMediaFilter) {
        const packageOptionIndex = Number(
          this.productMedia.dataset.packageOptionIndex || 1
        );
        const packageValue =
          variant.options?.[packageOptionIndex] || variant.option2 || "";
        const packageGroup = this.normalizeMediaGroup(packageValue);
        packageIds = medias
          .filter((media) => media.dataset.mediaGroup === packageGroup)
          .map((media) => String(media.dataset.mediaId));
      }

      const variantFeaturedId = variant.featured_media?.id
        ? String(variant.featured_media.id)
        : "";
      const productFeaturedId = this.productMedia.dataset.productFeaturedMediaId;

      if (packageIds.length === 0 && formatIds.length === 0) {
        const fallbackId =
          (variantFeaturedId && mediaById.has(variantFeaturedId)
            ? variantFeaturedId
            : "") ||
          (productFeaturedId && mediaById.has(productFeaturedId)
            ? productFeaturedId
            : "");
        if (fallbackId) packageIds = [fallbackId];
      }

      const orderedIds = [];
      const addMediaId = (mediaId) => {
        const id = String(mediaId || "");
        if (id && mediaById.has(id) && !orderedIds.includes(id)) {
          orderedIds.push(id);
        }
      };

      if (variantFeaturedId) addMediaId(variantFeaturedId);
      formatIds
        .filter((mediaId) => !sharedIds.includes(String(mediaId)))
        .forEach(addMediaId);
      packageIds
        .filter((mediaId) => !sharedIds.includes(String(mediaId)))
        .forEach(addMediaId);
      sharedIds.forEach(addMediaId);

      return orderedIds.length > 0 ? orderedIds : allIds;
    }

    renderFilteredMedia(initialItems, visibleMediaIds, isThumbnail) {
      if (!initialItems) return;

      const wrapper = isThumbnail
        ? this.productMedia.querySelector(
            ".m-product-media--slider__thumbnails .swiper-wrapper"
          )
        : this.productMedia.querySelector(".m-media-gallery__list");
      if (!wrapper) return;

      const itemsById = new Map(
        Array.from(initialItems).map((media) => [
          String(media.dataset.mediaId),
          media,
        ])
      );

      wrapper.replaceChildren();
      visibleMediaIds.forEach((mediaId, index) => {
        const media = itemsById.get(String(mediaId));
        if (!media) return;

        media.dataset.index = String(index);
        media.removeAttribute("aria-hidden");
        media.style.removeProperty("width");
        media.style.removeProperty("margin-right");
        media.style.removeProperty("transform");

        if (isThumbnail) {
          const isActive = index === 0;
          media.classList.toggle("swiper-slide-thumb-active", isActive);
          if (isActive) {
            media.setAttribute("aria-current", "true");
          } else {
            media.removeAttribute("aria-current");
          }
        } else {
          if (this.productMedia.dataset.layout === "layout-2") {
            media.classList.toggle("m-col-span-2", index % 3 === 0);
          }
          const productMedia = media.querySelector(".m-product-media");
          if (productMedia) {
            productMedia.style.setProperty("--animation-order", index + 1);
          }
        }

        wrapper.append(media);
      });
    }

    initQuantityHandlers() {
      if (this.quantityInputs.length <= 0) return;

      this.setQuantityBoundries();
      if (!this.hasAttribute("data-original-section-id")) {
        this.cartUpdateUnsubscriber = MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.cartUpdate,
          this.fetchQuantityRules.bind(this)
        );
      }
    }

    setQuantityBoundries() {
      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.setQuantityBoundries(this.sectionId, this.productId);
      });
    }

    fetchQuantityRules() {
      const currentVariantId = this.productForm?.variantIdInput?.value;
      if (!currentVariantId) return;

      this.querySelector('.quantity__rules-cart')?.classList.add('loading');

      fetch(
        `${this.getAttribute(
          "data-url"
        )}?variant=${currentVariantId}&section_id=${this.sectionId}`
      )
        .then((response) => response.text())
        .then((responseText) => {
          const parsedHTML = new DOMParser().parseFromString(
            responseText,
            "text/html"
          );
          this.updateQuantityRules(
            this.sectionId,
            this.productId,
            parsedHTML
          );
        })
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          this.querySelector('.quantity__rules-cart')?.classList.remove('loading');
        });
    }

    updateQuantityRules(sectionId, productId, parsedHTML) {
      if (this.quantityInputs.length <= 0) return;

      this.quantityInputs.forEach((input) => {
        const quantityInputWrapper = input.closest('m-quantity-input');
        quantityInputWrapper.updateQuantityRules(sectionId, productId, parsedHTML);
      });

      this.setQuantityBoundries();
    }

    addRecentViewedProduct() {
      const cookies = getCookie('m-recent-viewed-products')
      let products = cookies ? JSON.parse(cookies) : []
      if (products.indexOf(MinimogSettings.productHandle) === -1) {
        products.unshift(MinimogSettings.productHandle)
        products = products.slice(0, 20)
        setCookie('m-recent-viewed-products', JSON.stringify(products));
      }
    }

    onWindowLoad() {
      this.acc = [];
      MinimogTheme.CompareProduct && MinimogTheme.CompareProduct.setCompareButtonsState();
      MinimogTheme.Wishlist && MinimogTheme.Wishlist.setWishlistButtonsState();
      this.addRecentViewedProduct();

      addEventDelegate({
        context: this,
        selector: (window.__minimog_review_selector || '') + '.m-product-collapsible .jdgm-widget-actions-wrapper, .m-product-collapsible .spr-summary-actions-newreview',
        handler: (e) => {
          const index = e.target.closest('.m-product-collapsible').dataset.index
          setTimeout(() => {
            this.acc[Number(index)].setContentHeight()
          }, 300)
        },
        capture: true
      })
    }
  }

  customElements.define("product-info", ProductInfo);
}
